// Google Ads & GA4 Event Tracking Utility
// Google Ads ID: AW-17960834716

declare global {
  interface Window {
    gtag: (
      command: 'event' | 'config' | 'js',
      action: string,
      params?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
  }
}

// ============================================================
// Google Ads Conversion Tracking (AW-17960834716)
// ============================================================

// Generic Google Ads conversion event
export const trackGoogleAdsConversion = (
  conversionLabel: string,
  value?: number,
  currency?: string
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'conversion', {
      send_to: `AW-17960834716/${conversionLabel}`,
      value: value || 0,
      currency: currency || 'AUD',
    });
  }
};

// Demo Booking conversion — high value
export const trackDemoBookingConversion = (source?: string) => {
  if (typeof window.gtag !== 'undefined') {
    // Google Ads conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/demo_booking',
      value: 50,
      currency: 'AUD',
    });
    // GA4 event
    window.gtag('event', 'generate_lead', {
      event_category: 'Demo',
      event_label: 'Demo Booking',
      source: source || 'website',
      value: 50,
      currency: 'AUD',
    });
  }
};

// Contact Form submission conversion
export const trackContactFormConversion = (formName: string, formId?: string) => {
  if (typeof window.gtag !== 'undefined') {
    // Google Ads conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/contact_form',
      value: 10,
      currency: 'AUD',
    });
    // GA4 event
    window.gtag('event', 'generate_lead', {
      event_category: 'Form',
      event_label: formName,
      form_id: formId,
      value: 10,
      currency: 'AUD',
    });
  }
};

// Free Trial signup conversion — very high value
export const trackTrialSignupConversion = (source?: string) => {
  if (typeof window.gtag !== 'undefined') {
    // Google Ads conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/trial_signup',
      value: 75,
      currency: 'AUD',
    });
    // GA4 event
    window.gtag('event', 'sign_up', {
      event_category: 'Trial',
      event_label: 'Free Trial Signup',
      source: source || 'website',
      value: 75,
      currency: 'AUD',
    });
  }
};

// Newsletter subscription conversion
export const trackNewsletterConversion = (source: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Track newsletter subscription in GA4
    window.gtag('event', 'newsletter_signup', {
      event_category: 'engagement',
      event_label: source,
      value: 5
    });

    // Track Google Ads newsletter conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/nZXoCKWy1vobEJytsvRC',
      value: 5.0,
      currency: 'AUD'
    });

    // Track Google Ads Subscribe conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/JI7HCLCy1vobEJytsvRC',
      value: 1.0,
      currency: 'AUD'
    });
  }
};

// Pricing / Checkout button click conversion
export const trackPricingConversion = (planName: string, price: number, billingCycle: string) => {
  if (typeof window.gtag !== 'undefined') {
    // Google Ads conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/pricing_click',
      value: price,
      currency: 'AUD',
    });
    // GA4 event
    window.gtag('event', 'begin_checkout', {
      event_category: 'Pricing',
      event_label: planName,
      value: price,
      currency: 'AUD',
      billing_cycle: billingCycle,
    });
  }
};

// CTA button click tracking
export const trackCTAClickConversion = (buttonName: string, location: string) => {
  if (typeof window.gtag !== 'undefined') {
    // Google Ads conversion
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/cta_click',
      value: 1,
      currency: 'AUD',
    });
    // GA4 event
    window.gtag('event', 'cta_click', {
      event_category: 'CTA',
      event_label: buttonName,
      location,
    });
  }
};

// Phone call click conversion
export const trackPhoneCallConversion = () => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'conversion', {
      send_to: 'AW-17960834716/phone_call',
      value: 30,
      currency: 'AUD',
    });
  }
};

// ============================================================
// GA4 Standard Events (kept for compatibility)
// ============================================================

export const trackPageView = (pagePath: string, pageTitle: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

export const trackFormSubmission = (formName: string, formId?: string) => {
  trackContactFormConversion(formName, formId);
};

export const trackDemoBooking = (source?: string) => {
  trackDemoBookingConversion(source);
};

export const trackCTAClick = (buttonName: string, location: string) => {
  trackCTAClickConversion(buttonName, location);
};

export const trackPricingClick = (planName: string, price: number, billingCycle: string) => {
  trackPricingConversion(planName, price, billingCycle);
};

export const trackNewsletterSignup = (source: string) => {
  trackNewsletterConversion(source);
};

export const trackResourceDownload = (resourceName: string, resourceType: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'file_download', {
      event_category: 'Resources',
      event_label: resourceName,
      file_type: resourceType,
    });
  }
};

export const trackVideoPlay = (videoTitle: string, videoDuration?: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'video_start', {
      event_category: 'Video',
      event_label: videoTitle,
      video_duration: videoDuration,
    });
  }
};

export const trackBlogRead = (postTitle: string, postSlug: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'view_item', {
      event_category: 'Blog',
      event_label: postTitle,
      content_id: postSlug,
    });
  }
};

export const trackSocialShare = (platform: string, contentType: string, contentTitle: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'share', {
      event_category: 'Social',
      method: platform,
      content_type: contentType,
      item_id: contentTitle,
    });
  }
};

export const trackScrollDepth = (percentage: number, pagePath: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'scroll', {
      event_category: 'Engagement',
      event_label: `${percentage}%`,
      page_path: pagePath,
    });
  }
};

export const trackOutboundLink = (url: string, linkText: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'click', {
      event_category: 'Outbound Link',
      event_label: linkText,
      link_url: url,
    });
  }
};

export const trackError = (errorMessage: string, errorLocation: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'exception', {
      description: errorMessage,
      fatal: false,
      location: errorLocation,
    });
  }
};

export const trackTimeOnPage = (seconds: number, pagePath: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'timing_complete', {
      event_category: 'Engagement',
      event_label: pagePath,
      value: seconds,
    });
  }
};

export const trackCustomConversion = (
  eventName: string,
  eventParams: Record<string, unknown>
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, eventParams);
  }
};
